import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import * as api from './api';
import { ApiError } from './api/client';
import { Icon, type IconName } from './components/Icon';
import { MemberBar } from './components/MemberBar';
import { ConfirmHost, ToastHost } from './components/Overlays';
import { TimerLayer } from './components/TimerLayer';
import { Spinner, cx } from './components/ui';
import { queryKeys } from './state/queries';
import { useShareLocationWhileOpen } from './state/shareLocation';
import { useThemeDriver } from './state/useTheme';
import { useIdle } from './state/useIdle';
import { useSSE } from './state/useSSE';
import { Calendar } from './views/Calendar';
import { Chores } from './views/Chores';
import { Dashboard } from './views/Dashboard';
import { Info } from './views/Info';
import { Lists } from './views/Lists';
import { Login } from './views/Login';
import { Meals } from './views/Meals';
import { Screensaver } from './views/Screensaver';
import { Settings } from './views/Settings';

const NAV: Array<{ to: string; label: string; icon: IconName; end: boolean }> = [
  { to: '/', label: 'Today', icon: 'today', end: true },
  { to: '/calendar', label: 'Calendar', icon: 'calendar', end: false },
  { to: '/meals', label: 'Meals', icon: 'meal', end: false },
  { to: '/lists', label: 'Lists', icon: 'list', end: false },
  { to: '/chores', label: 'Chores', icon: 'chores', end: false },
  { to: '/info', label: 'Info', icon: 'shield', end: false },
  { to: '/settings', label: 'Settings', icon: 'settings', end: false },
];

export function App() {
  const me = useQuery({ queryKey: queryKeys.me, queryFn: api.getMe, retry: false });
  const authed = me.isSuccess;

  // Only hold the stream open once authenticated — an unauthenticated EventSource would
  // reconnect against a 401 forever.
  const { connected } = useSSE(authed);

  const { data: household } = useQuery({
    queryKey: queryKeys.household,
    queryFn: api.getHousehold,
    enabled: authed,
  });

  // A phone in a pocket must never wake up showing a slideshow — the screensaver is a
  // wall-display behaviour, so gate it on a viewport that could plausibly be mounted.
  const bigScreen = useMediaQuery('(min-width: 768px)');
  const screensaverOn = Boolean(authed && household?.screensaverEnabled && bigScreen);
  const idle = useIdle((household?.screensaverIdleMinutes ?? 3) * 60_000, screensaverOn);

  // Share this device's location while the app is open (opt-in, per device).
  useShareLocationWhileOpen(authed);

  // Auto light/dark: follow the household's sunrise/sunset (falls back to the OS).
  const { data: weather } = useQuery({
    queryKey: queryKeys.weather,
    queryFn: api.getWeather,
    enabled: authed,
    refetchInterval: 30 * 60 * 1000,
  });
  useThemeDriver(weather?.sun ?? null, household?.timezone ?? 'local');

  if (me.isLoading) return <Spinner label="starting" />;
  if (me.error instanceof ApiError && me.error.status === 401) return <Login />;
  if (me.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
        <h1 className="font-display text-[22px] text-primary">Can't reach the server</h1>
        <p className="eyebrow">Retrying automatically</p>
      </div>
    );
  }

  return (
    <div className="flex h-[var(--app-height,100dvh)] flex-col md:flex-row">
      <Rail connected={connected} />

      <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain pt-[env(safe-area-inset-top)] md:pt-0">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/meals" element={<Meals />} />
          <Route path="/lists" element={<Lists />} />
          <Route path="/chores" element={<Chores />} />
          <Route path="/info" element={<Info />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <BottomNav />

      {/* Always mounted so the kitchen timer keeps ticking and can chime on any view. */}
      <TimerLayer />
      <ToastHost />
      <ConfirmHost />

      {idle && screensaverOn && (
        <Screensaver intervalSeconds={household?.screensaverIntervalSeconds ?? 20} />
      )}
    </div>
  );
}

/** Tiny matchMedia binding — one call site, not worth a dependency. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (): void => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * A persistent left rail rather than Alchemist2's topbar: this hangs on a wall, where a
 * fixed set of big always-visible targets beats a compact nav you have to aim at.
 */
function Rail({ connected }: { connected: boolean }) {
  return (
    <aside className="hidden w-56 shrink-0 flex-col border-r border-border-subtle bg-surface/40 md:flex">
      <div className="flex items-center gap-2 px-5 py-6">
        <span className="font-display text-[15px] uppercase text-primary" style={{ letterSpacing: '0.22em' }}>
          Pacer
        </span>
        <span
          className={cx(
            'size-1.5 rounded-full transition-colors duration-500',
            connected ? 'bg-success' : 'bg-muted',
          )}
          title={connected ? 'Live' : 'Reconnecting'}
          aria-label={connected ? 'Live' : 'Reconnecting'}
        />
      </div>

      <nav className="flex flex-col gap-0.5 px-3">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              cx(
                'touch relative flex items-center gap-3 rounded-md px-3 text-[14px] transition-colors duration-150',
                // The active marker is a hairline ::before rail, not a filled block.
                'before:absolute before:top-1/2 before:-left-3 before:h-6 before:w-[2px] before:-translate-y-1/2 before:rounded-r before:transition-colors before:duration-150',
                isActive
                  ? 'bg-[var(--color-accent-fade)] text-primary before:bg-accent'
                  : 'text-muted before:bg-transparent hover:bg-surface hover:text-secondary',
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon name={n.icon} size={16} className={isActive ? 'text-accent' : undefined} />
                {n.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto border-t border-border-subtle p-3">
        <MemberBar />
      </div>
    </aside>
  );
}

function BottomNav() {
  return (
    <nav className="flex shrink-0 border-t border-border-subtle bg-surface pb-[var(--nav-inset)] md:hidden">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          className={({ isActive }) =>
            cx(
              'flex flex-1 flex-col items-center gap-1 pt-2 pb-0.5 transition duration-150 active:scale-90',
              isActive ? 'text-accent' : 'text-muted',
            )
          }
        >
          <Icon name={n.icon} size={19} />
          <span className="font-mono text-[10px] uppercase min-[500px]:text-[12px]" style={{ letterSpacing: '0.1em' }}>
            {n.label}
          </span>
        </NavLink>
      ))}
    </nav>
  );
}
